import React, { useCallback, useEffect, useRef, useState } from "react";
import "./App.css";
import "./components/Inauguration.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";

import Header from "./components/Header";
import Footer from "./components/Footer";
import Loader from "./components/Loader";
import { EdgeFireworks, InaugurationGate } from "./components/Inauguration";
import { ceremonyAlreadySeen, ceremonyGateDecision, loadCeremonyEnabled, markCeremonySeen } from "./lib/ceremony";
import BrandLogo from "./components/BrandLogo";
import BrandJump from "./components/BrandJump";
import Watermark from "./components/Watermark";
import CursorGlow from "./components/CursorGlow";
import InstallPrompt from "./components/InstallPrompt";
import { LangProvider } from "./i18n";
import { Headset } from "lucide-react";

import Home from "./pages/Home";
import About from "./pages/About";
import Colleges from "./pages/Colleges";
import CollegeDetail from "./pages/CollegeDetail";
import Admissions from "./pages/Admissions";
import Gallery from "./pages/Gallery";
import Circulars from "./pages/Circulars";
import Careers from "./pages/Careers";
import UsefulLinks from "./pages/UsefulLinks";
import Login from "./pages/Login";
import Contact from "./pages/Contact";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);
  return null;
}

// Different cinematic transition per page
const routeFx = (p) => p === "/" ? "curtain" : p.startsWith("/about") ? "iris" : p.startsWith("/colleges/") ? "zoom" : p.startsWith("/colleges") ? "blinds"
  : p.startsWith("/admissions") ? "rise" : p.startsWith("/gallery") ? "shutter" : p.startsWith("/circulars") ? "flip" : p.startsWith("/careers") ? "diagonal"
  : p.startsWith("/useful-links") ? "split" : p.startsWith("/login") ? "lock" : "doors";

function Shell() {
  const location = useLocation();
  const [sweep, setSweep] = useState(false);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    setSweep(true);
    const t = setTimeout(() => setSweep(false), 700);
    return () => clearTimeout(t);
  }, [location.pathname]);
  const fx = routeFx(location.pathname);
  return (
    <>
      <ScrollToTop />
      <BrandJump />
      <Watermark />
      <Header />
      {sweep && (
        <div className={`route-fx route-fx-${fx}`} data-testid="route-transition" data-fx={fx} aria-hidden="true">
          <div className="route-fx-panel a" /><div className="route-fx-panel b" />
          <div className="route-fx-icon">
            <div className="route-fx-logo">
              <BrandLogo size="auto" plate />
            </div>
            <div className="route-fx-pulse" />
          </div>
        </div>
      )}
      <main key={location.pathname} className={`site-main route-enter route-enter-${fx}`} style={{ minHeight: "60vh" }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/colleges" element={<Colleges />} />
          <Route path="/colleges/:slug" element={<CollegeDetail />} />
          <Route path="/admissions" element={<Admissions />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/circulars" element={<Circulars />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/useful-links" element={<UsefulLinks />} />
          <Route path="/login" element={<Login />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
      </main>
      <Footer />
      <InstallPrompt />
      <Link to="/contact" className="fab" data-testid="help-fab" aria-label="Contact support"><Headset size={26} /></Link>
    </>
  );
}

function rememberLoader() {
  try {
    sessionStorage.setItem("apmercl_seen_loader", "1");
  } catch {
    /* private mode */
  }
}

function App() {
  const [loading, setLoading] = useState(true);
  const [gate, setGate] = useState(() => {
    if (ceremonyAlreadySeen()) return "site";
    // Crawlers skip without waiting. Everyone else waits for a strict On flag.
    if (ceremonyGateDecision(false, true) === "site") return "site";
    return "pending";
  });
  const [fireworks, setFireworks] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("apmercl_seen_loader") || ceremonyAlreadySeen()) setLoading(false);
    } catch {
      setLoading(false);
    }
    if (gate !== "pending") return undefined;
    let cancelled = false;
    loadCeremonyEnabled().then((enabled) => {
      if (cancelled) return;
      setGate(ceremonyGateDecision(ceremonyAlreadySeen(), enabled) === "gate" ? "gate" : "site");
    });
    return () => {
      cancelled = true;
    };
  }, [gate]);

  useEffect(() => {
    if (gate === "site") return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [gate]);

  const done = () => {
    rememberLoader();
    setLoading(false);
  };

  const finishCeremony = useCallback(() => {
    markCeremonySeen();
    rememberLoader();
    setLoading(false);
    setGate("site");
    setFireworks(true);
  }, []);

  const covered = gate !== "site";

  return (
    <div className="App">
      <LangProvider>
      <CursorGlow />
      <div inert={covered ? "" : undefined} aria-hidden={covered ? "true" : undefined}>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </div>
      {loading && gate === "site" && <Loader onDone={done} />}
      {gate === "pending" && (
        <div className="inauguration-hold" data-testid="inauguration-hold">
          <span className="inaug-sr">Loading the APMERCL website</span>
        </div>
      )}
      {gate === "gate" && <InaugurationGate onComplete={finishCeremony} />}
      {fireworks && <EdgeFireworks onDone={() => setFireworks(false)} />}
      </LangProvider>
    </div>
  );
}

export default App;
